import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Users, DollarSign, ArrowRight, FileText, Gift } from 'lucide-react';
import { formatCurrency, formatDate, getRegistrationFeeLabel } from '../../utils/helpers';

/**
 * Financial Dashboard - Landing page for financial overview
 * Uses existing data structures (expenses + walletTransactions)
 * Links to existing components (FinancialReports, PaymentManagement)
 */
const FinancialDashboard = ({
  expenses = [],
  walletTransactions = [],
  players = [],
  paymentSubmissions = [],
  onNavigate,
  onApproveExpense,
  onDenyExpense,
  onApprovePayment,
  onDenyPayment,
  currentUser,
  canApprove,  // Added prop for treasurer permission check
  userRole,     // Added prop for displaying user role
  clubName
}) => {

  // Treasurer role check - use passed prop or default to false
  const isTreasurer = canApprove !== undefined ? canApprove : false;
  const displayRole = userRole || 'Admin';

  // Calculate financial summary from existing data
  const summary = useMemo(() => {
    try {
      // Debug logging
      console.log('🔍 FinancialDashboard Debug:', {
        expensesCount: expenses?.length,
        walletTransactionsCount: walletTransactions?.length,
        playersCount: players?.length,
        expenses: expenses,
        players: players,
        walletTransactions: walletTransactions
      });

      // 1. Calculate Actual Revenue (Payments)
      const paymentsReceived = (walletTransactions || []).filter(txn =>
        txn.type === 'payment' &&
        txn.status === 'approved' &&
        txn.amount < 0 &&
        !txn.isDonation // Exclude donations from reported revenue
      );
      const totalCollected = Math.abs(paymentsReceived.reduce((sum, txn) => sum + (txn.amount || 0), 0));

      // 2. Calculate Waived Amounts
      const waivers = (walletTransactions || []).filter(txn =>
        txn.type === 'waiver' && txn.status === 'approved' && txn.amount < 0
      );
      const totalWaived = Math.abs(waivers.reduce((sum, txn) => sum + (txn.amount || 0), 0));

      // Total Received = Money actually received (excluding waivers)
      const totalReceived = totalCollected;

      console.log('💵 Total Received:', totalReceived);
      console.log('🎁 Total Waived:', totalWaived);

      // Actual Expenses = Non-registration, non-dividable expenses (money club spent)
      const actualExpenses = (expenses || []).filter(e =>
        e.approvalStatus === 'approved' &&
        !e.isInitialDonation &&
        !e.isRegistrationFee &&
        !e.isDividable &&
        !e.isDonation // Exclude donation records (shouldn't exist anymore, but for safety)
      );
      const totalExpenses = actualExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

      // Count received items for display (Initial Donations + game fees that were collected)
      const receivedItems = (expenses || []).filter(e =>
        e.approvalStatus === 'approved' &&
        (e.isInitialDonation || e.isRegistrationFee || e.isDividable)
      );
      const receivedCount = receivedItems.length;

      // Pending Expenses = Expenses awaiting approval
      const pendingExpenses = (expenses || []).filter(e =>
        e.approvalStatus === 'pending'
      );
      const totalPending = pendingExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

      // Receivables = Money players still owe
      // Calculate DIRECTLY from expenses (same as FinancialReports)
      // This ensures consistency across all financial views
      console.log('💰 Calculating Receivables Balances (from expenses directly)...');

      // EXPECTED RECEIVABLES = Total Initial Donations assigned to players
      // Filter to only include ACTIVE (approved) players
      const activePlayerIds = new Set((players || []).filter(p => p.clubStatus === 'approved').map(p => p.id));

      const initialDonationAmounts = (expenses || []).filter(e => {
        // Must be approved and correct type
        if (!((e.isInitialDonation || e.isRegistrationFee) && e.approvalStatus === 'approved')) {
          return false;
        }

        // Check if player assigned to this fee is still active
        if (e.splitAmong && Array.isArray(e.splitAmong)) {
          return e.splitAmong.some(id => activePlayerIds.has(id));
        }

        return true;
      });

      const totalExpectedReceivables = initialDonationAmounts.reduce((sum, e) => sum + (e.amount || 0), 0);
      console.log(`  📝 Total Expected Receivables (Initial Donations): $${totalExpectedReceivables}`);
      console.log(`  💵 Total Collected: $${totalCollected}`);

      // RECEIVABLES (Outstanding) = Expected Receivables - Collected - Waived
      const totalReceivables = Math.max(0, totalExpectedReceivables - totalCollected - totalWaived);
      console.log(`💰 Total Receivables: $${totalReceivables}`);

      // Count players with ACTUAL unpaid balances
      let unpaidPlayerCount = 0;

      // Iterate through all active players to check their individual balance
      activePlayerIds.forEach(playerId => {
        // 1. Calculate what this player owes (from Initial Donations)
        const playerFees = initialDonationAmounts.filter(e =>
          e.splitAmong && e.splitAmong.includes(playerId)
        );
        const playerOwed = playerFees.reduce((sum, e) => sum + (e.amount || 0), 0);

        // If player doesn't owe anything, skip
        if (playerOwed <= 0) return;

        // 2. Calculate what this player has paid
        const playerPayments = (walletTransactions || []).filter(txn =>
          txn.type === 'payment' &&
          txn.status === 'approved' &&
          txn.amount < 0 &&
          txn.userId === playerId
        );
        const playerPaid = Math.abs(playerPayments.reduce((sum, txn) => sum + (txn.amount || 0), 0));

        // 3. Calculate waivers for this player
        const playerWaivers = (walletTransactions || []).filter(txn =>
          txn.type === 'waiver' &&
          txn.status === 'approved' &&
          txn.amount < 0 &&
          txn.userId === playerId
        );
        const playerWaived = Math.abs(playerWaivers.reduce((sum, txn) => sum + (txn.amount || 0), 0));

        // 4. Determine balance
        const balance = Math.max(0, playerOwed - playerPaid - playerWaived);

        if (balance > 1) { // Use > 1 to avoid penny rounding issues
          unpaidPlayerCount++;
        }
      });

      const receivablesPlayerCount = unpaidPlayerCount;

      const netBalance = totalReceived - totalExpenses;

      // Expected Total = Received + Receivables (Excluding Waived)
      // We do not include waived amounts because we do not expect to collect them
      const expectedTotal = totalReceived + totalReceivables;

      // Collection Rate = Received / Expected
      const collectionRate = expectedTotal > 0 ? (totalReceived / expectedTotal) * 100 : 100;

      return {
        totalReceived,
        totalExpenses,
        totalReceivables,
        totalExpectedReceivables,
        totalCollected,
        totalWaived,
        netBalance,
        expectedTotal,
        collectionRate,
        receivedCount,
        expenseCount: actualExpenses.length,
        pendingExpenses: pendingExpenses,
        totalPending: totalPending,
        pendingCount: pendingExpenses.length,
        // Pending payment submissions
        pendingPayments: (paymentSubmissions || []).filter(p => p.status === 'pending'),
        totalPendingPayments: (paymentSubmissions || []).filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.amount || 0), 0),
        pendingPaymentsCount: (paymentSubmissions || []).filter(p => p.status === 'pending').length,
        receivablesCount: receivablesPlayerCount
      };
    } catch (error) {
      console.error('❌ Error calculating financial summary:', error);
      return {
        totalReceived: 0,
        totalExpenses: 0,
        totalReceivables: 0,
        totalExpectedReceivables: 0,
        totalCollected: 0,
        totalWaived: 0,
        netBalance: 0,
        expectedTotal: 0,
        collectionRate: 0,
        receivedCount: 0,
        expenseCount: 0,
        pendingExpenses: [],
        totalPending: 0,
        pendingCount: 0,
        pendingPayments: [],
        totalPendingPayments: 0,
        pendingPaymentsCount: 0,
        receivablesCount: 0
      };
    }
  }, [expenses, walletTransactions, players, paymentSubmissions]);

  return (
    <div className="max-w-7xl mx-auto">
      <button
        onClick={() => onNavigate('overview')}
        className="text-gray-600 mb-4 hover:text-gray-800 flex items-center gap-2"
      >
        ← Back to Overview
      </button>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Financial Dashboard</h1>
        <p className="text-gray-600">Overview of club finances and player balances</p>
      </div>

      {/* Pending Expenses Section */}
      {summary.pendingCount > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-200 rounded-lg">
                <svg className="w-6 h-6 text-yellow-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-yellow-900">Pending Expenses</h3>
                <p className="text-sm text-yellow-700">
                  {summary.pendingCount} expense{summary.pendingCount !== 1 ? 's' : ''} awaiting approval ({formatCurrency(summary.totalPending)})
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {summary.pendingExpenses.map(expense => (
              <div key={expense.id} className="bg-white rounded-lg p-4 border-2 border-yellow-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900">{expense.description || expense.type}</h4>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded">
                        Pending
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mb-2">{formatCurrency(expense.amount)}</p>
                    <div className="text-sm text-gray-600 space-y-1">
                      {expense.payee && <p>Payee: <span className="font-medium">{expense.payee}</span></p>}
                      {expense.date && <p>Date: {formatDate(expense.date)}</p>}
                      {expense.createdByName && <p>Submitted by: <span className="font-medium">{expense.createdByName}</span></p>}
                      {expense.isDividable && <p className="text-blue-600 font-medium">🔄 Dividable among squad</p>}
                      {expense.isInitialDonation && <p className="text-purple-600 font-medium">💰 {getRegistrationFeeLabel({ name: clubName })}</p>}
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    {/* Only show buttons for treasurers, not for the expense creator */}
                    {expense.createdBy === currentUser?.email ? (
                      <div className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium border border-amber-300">
                        <p className="font-semibold">⚠️ You submitted this</p>
                        <p className="text-xs">Treasurer must approve</p>
                      </div>
                    ) : canApprove ? (
                      // Only show approve/deny buttons for treasurers
                      <>
                        {onApproveExpense && (
                          <button
                            onClick={() => onApproveExpense(expense)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors flex items-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Approve
                          </button>
                        )}
                        {onDenyExpense && (
                          <button
                            onClick={() => onDenyExpense(expense)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors flex items-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Deny
                          </button>
                        )}
                      </>
                    ) : (
                      // Non-treasurers see this message
                      <div className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm border border-gray-300">
                        <p className="font-medium flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          View Only
                        </p>
                        <p className="text-xs">Treasurer approval required</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Payment Submissions Section */}
      {summary.pendingPaymentsCount > 0 && (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-200 rounded-lg">
                <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-blue-900">Pending Payment Submissions</h3>
                <p className="text-sm text-blue-700">
                  {summary.pendingPaymentsCount} payment{summary.pendingPaymentsCount !== 1 ? 's' : ''} awaiting approval ({formatCurrency(summary.totalPendingPayments)})
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {summary.pendingPayments.map(payment => (
              <div key={payment.id} className="bg-white rounded-lg p-4 border-2 border-blue-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900">{payment.playerName}</h4>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                        Pending Review
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mb-2">{formatCurrency(payment.amount)}</p>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p><span className="font-medium">Method:</span> {payment.paymentMethod}</p>
                      <p><span className="font-medium">Payment Date:</span> {formatDate(payment.paymentDate)}</p>
                      {payment.transactionId && (
                        <p><span className="font-medium">Transaction ID:</span> {payment.transactionId}</p>
                      )}
                      {payment.notes && (
                        <p><span className="font-medium">Notes:</span> {payment.notes}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Submitted: {formatDate(payment.submittedAt)} at {new Date(payment.submittedAt).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    {payment.receiptURL && (
                      <a
                        href={payment.receiptURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded text-sm font-semibold border border-blue-600 text-center"
                      >
                        📄 View Receipt
                      </a>
                    )}
                    {/* Only show approve/deny buttons for treasurers */}
                    {isTreasurer ? (
                      <>
                        {onApprovePayment && (
                          <button
                            onClick={() => {
                              const note = prompt('Add an optional note for this approval (e.g., "Received via Zelle"):');
                              if (note === null) return; // Cancelled
                              onApprovePayment(payment, note);
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Approve
                          </button>
                        )}
                        {onDenyPayment && (
                          <button
                            onClick={() => {
                              const reason = prompt('Please provide a reason for denying this payment:');
                              if (reason === null) return; // Cancelled
                              if (!reason.trim()) {
                                alert('A reason is required to deny a payment.');
                                return;
                              }
                              onDenyPayment(payment, reason);
                            }}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-colors flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Deny
                          </button>
                        )}
                      </>
                    ) : (
                      // Non-treasurers see view-only message
                      <div className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm border border-gray-300">
                        <p className="font-medium flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          View Only
                        </p>
                        <p className="text-xs">Treasurer approval required</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Financial Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        {/* Received Card */}
        <div
          onClick={() => onNavigate('financial-reports')}
          className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="text-green-600" size={24} />
            </div>
            <ArrowRight className="text-green-600" size={20} />
          </div>
          <p className="text-sm font-medium text-green-900 mb-1">Total Received</p>
          <p className="text-3xl font-bold text-green-700">{formatCurrency(summary.totalReceived)}</p>
          <p className="text-xs text-green-600 mt-2">{summary.receivedCount} received item{summary.receivedCount !== 1 ? 's' : ''}</p>
        </div>

        {/* Expenses Card */}
        <div
          onClick={() => onNavigate('financial-reports')}
          className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <TrendingDown className="text-red-600" size={24} />
            </div>
            <ArrowRight className="text-red-600" size={20} />
          </div>
          <p className="text-sm font-medium text-red-900 mb-1">Total Expenses</p>
          <p className="text-3xl font-bold text-red-700">{formatCurrency(summary.totalExpenses)}</p>
          <p className="text-xs text-red-600 mt-2">{summary.expenseCount} expense{summary.expenseCount !== 1 ? 's' : ''}</p>
        </div>

        {/* Waived Fees Card */}
        <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 border-2 border-purple-200 rounded-xl p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Gift className="text-purple-600" size={24} />
            </div>
          </div>
          <p className="text-sm font-medium text-purple-900 mb-1">Total Waived</p>
          <p className="text-3xl font-bold text-purple-700">{formatCurrency(summary.totalWaived)}</p>
          <p className="text-xs text-purple-600 mt-2">Fees waived by admin</p>
        </div>

        {/* Receivables Card */}
        <div
          onClick={() => onNavigate('payment-management')}
          className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Users className="text-orange-600" size={24} />
            </div>
            <ArrowRight className="text-orange-600" size={20} />
          </div>
          <p className="text-sm font-medium text-orange-900 mb-1">Receivables</p>
          <p className="text-3xl font-bold text-orange-700">{formatCurrency(summary.totalReceivables)}</p>
          <p className="text-xs text-orange-600 mt-2">{summary.receivablesCount} player{summary.receivablesCount !== 1 ? 's' : ''} owe money</p>
        </div>

        {/* Net Balance Card */}
        <div className={`bg-gradient-to-br border-2 rounded-xl p-6 ${summary.netBalance >= 0
          ? 'from-blue-50 to-indigo-50 border-blue-200'
          : 'from-gray-50 to-slate-50 border-gray-300'
          }`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`p-3 rounded-lg ${summary.netBalance >= 0 ? 'bg-blue-100' : 'bg-gray-200'
              }`}>
              <DollarSign className={summary.netBalance >= 0 ? 'text-blue-600' : 'text-gray-600'} size={24} />
            </div>
          </div>
          <p className={`text-sm font-medium mb-1 ${summary.netBalance >= 0 ? 'text-blue-900' : 'text-gray-900'
            }`}>Net Balance</p>
          <p className={`text-3xl font-bold ${summary.netBalance >= 0 ? 'text-blue-700' : 'text-gray-700'
            }`}>
            {formatCurrency(summary.netBalance)}
          </p>
          <p className={`text-xs mt-2 ${summary.netBalance >= 0 ? 'text-blue-600' : 'text-gray-600'
            }`}>
            Received - Expenses
          </p>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Expected Total */}
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FileText className="text-purple-600" size={20} />
            </div>
            <h3 className="font-bold text-gray-900">Expected Total Received</h3>
          </div>
          <p className="text-2xl font-bold text-purple-700 mb-2">{formatCurrency(summary.expectedTotal)}</p>
          <p className="text-sm text-gray-600">
            {formatCurrency(summary.totalReceived)} received + {formatCurrency(summary.totalReceivables)} outstanding + {formatCurrency(summary.totalWaived)} waived
          </p>
        </div>

        {/* Collection Rate */}
        <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <TrendingUp className="text-indigo-600" size={20} />
            </div>
            <h3 className="font-bold text-gray-900">Collection Rate</h3>
          </div>
          <p className="text-2xl font-bold text-indigo-700 mb-2">{summary.collectionRate.toFixed(1)}%</p>
          <p className="text-sm text-gray-600 mb-3">
            {formatCurrency(summary.totalReceived)} of {formatCurrency(summary.expectedTotal)} collected
          </p>

          {/* Progress Bar */}
          <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="bg-indigo-600 h-full transition-all duration-500"
              style={{ width: `${Math.min(summary.collectionRate, 100)}%` }}
            />
          </div>
        </div>
      </div>



      {/* Quick Actions */}
      <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
        <h3 className="font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={() => onNavigate('expense-management')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold transition-colors"
          >
            <DollarSign size={20} />
            Add Expense
          </button>

          <button
            onClick={() => onNavigate('payment-management')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors"
          >
            <Users size={20} />
            Manage Payments
          </button>

          <button
            onClick={() => onNavigate('financial-reports')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
          >
            <FileText size={20} />
            View Reports
          </button>

          <button
            onClick={() => onNavigate('registration-report')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold transition-colors"
          >
            <FileText size={20} />
            Registration Report
          </button>
        </div>
      </div>

      {/* Alert for High Outstanding */}
      {summary.totalReceivables > 500 && (
        <div className="mt-6 bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Users className="text-orange-600" size={20} />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-orange-900 mb-1">
                {formatCurrency(summary.totalReceivables)} in Receivables
              </h4>
              <p className="text-sm text-orange-800 mb-3">
                {summary.receivablesCount} player{summary.receivablesCount !== 1 ? 's have' : ' has'} unpaid charges.
                Consider sending payment reminders.
              </p>
              <button
                onClick={() => onNavigate('payment-management')}
                className="text-sm font-semibold text-orange-600 hover:text-orange-800 flex items-center gap-1"
              >
                View Details
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialDashboard;